import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Fab,
  styled,
  Typography,
  useTheme
} from "@mui/material"

import {
  ExpandMoreRounded
} from "@mui/icons-material"

import HKVMarkdown from "../components/HKVMarkdown/4.0"

import Cards from "../components/Cards/Cards"
import Infographic from "../components/Infographic/Infographic"
import List from "../components/List"

import { fetchLocalFile, GetPopular, GetThemes } from "../store/reducers/functions"

import { groupByCategoryKey, scrollToElement } from "../lib/utils"

import PackageJson from "../../package.json"

import moment from "moment"

const StyledHeadTypography = styled(Typography, {
})(({ theme }) => {
  return {
    width: "100%",
    fontSize: theme.typography.pxToRem(40),
    fontWeight: 700,
    margin: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      fontSize: theme.typography.pxToRem(34)
    },
    [theme.breakpoints.down(550)]: {
      fontSize: theme.typography.pxToRem(30)
    },
    [theme.breakpoints.down(450)]: {
      fontSize: theme.typography.pxToRem(28)
    }
  }
})

const StyledSection = styled("section", {
})(({ theme }) => {
  return {
    width: `calc(100% / 10 * 7 - var(--size-gutter))`,
    "&:first-of-type": {
      width: `calc(100% / 10 * 3 - var(--size-gutter))`,
      marginTop: "0px !important"
    },
    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginTop: `${theme.spacing(5)} !important`,
      "&:first-of-type": {
        width: "100%"
      }
    }
  }
})

const StyledBox = styled(Box, {
})(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    "& > div": {
      width: "50%",
    },
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      "& > div": {
        width: "100%"
      }
    }
  }
})

const Home = (props) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const {
  } = props

  const [expanded, setExpanded] = useState()

  const [themes, setThemes] = useState([])
  const [popular, setPopular] = useState([])
  const [links, setLinks] = useState([])

  const [introMD, setIntroMD] = useState()
  const [overLVOMD, setOverLVOMD] = useState()
  const [waaromLVOMD, setWaaromLVOMD] = useState()

  useEffect(() => {
    dispatch(fetchLocalFile("/static/markdown/intro.md?20260429", "text"))
      .then((md) => {
        setIntroMD({ key: moment().valueOf(), source: md })
      })

    dispatch(fetchLocalFile("/static/markdown/over_lvo-short.md?20260429", "text"))
      .then((md) => {
        setOverLVOMD({ key: moment().valueOf(), source: md })
      })

    dispatch(fetchLocalFile("/static/markdown/waarom_lvo.md?20260429", "text"))
      .then((md) => {
        setWaaromLVOMD({ key: moment().valueOf(), source: md })
      })
  }, [])

  useEffect(() => {
    dispatch(GetThemes())
      .then((json) => {
        const data = Array.isArray(json) ? json : []
        const groupedThemes = groupByCategoryKey(data)
        setThemes(groupedThemes)
      })

    dispatch(GetPopular())
      .then((json) => {
        setPopular(Array.isArray(json) ? json.map((j, i) => {
          return {
            public: j.public,
            favorite: true,
            name: j.question,
            link: j.url
          }
        }) : [])
      })
  }, [])

  useEffect(() => {
    if (themes?.length > 0 && popular?.length > 0) {
      setLinks(popular.concat(themes.map((theme, ix) => {
        return {
          favorite: false,
          name: theme.category.name,
          link: `/themas/${theme.category.key}`
        }
      })))
    }
  }, [themes, popular])

  const renderCallToAction = () => {
    return (
      <AppBar
        elevation={0}
        position="relative"
        sx={{
          background: theme.palette.grey[100],
          color: theme.palette.text.primary,
          flexDirection: "row",
          justifyContent: "center",
          height: 44
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            maxWidth: 1200,
            margin: theme.spacing(0, 2)
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h4" sx={{ marginRight: theme.spacing(1) }}>Ga direct naar uitgelichte thema's</Typography>
            <Fab
              title="Scroll naar uitgelichte thema's"
              color="primary"
              size="small"
              sx={{ boxShadow: "unset !important", width: 28, height: 28, minHeight: 28, minWidth: 28 }}
              onClick={() => scrollToElement("thema's uitgelicht", -40, "smooth")}
            >
              <ExpandMoreRounded />
            </Fab>
          </div>
          <Typography variant="caption">{`Versie: ${PackageJson.version}`}</Typography>
        </div>
      </AppBar>
    )
  }

  const createThemeLink = useCallback((card) => card?.key ? `/themas/${card.category.key}/${card.key}` : "themas", [])

  return (
    <div>
      <section id="infographic" className="brick">
        <div className="brick-body">
          <StyledHeadTypography variant="h1">
            Landelijke Voorziening Overstromingsinformatie
          </StyledHeadTypography>
          <div style={{ margin: theme.spacing(2) }}>
            <HKVMarkdown
              key={introMD?.key}
              source={introMD?.source}
              className="rich-text-full"
            />
          </div>
          <StyledSection>
            <div>
              <List
                title="Direct naar"
                items={links.filter(l => l.favorite === true)}
                variant="yellow"
              />
              <List
                items={links.filter(l => l.favorite === false)}
                variant="blue"
              />
            </div>
          </StyledSection>
          <StyledSection>
            <Typography variant="h3" align="center">Oorzaken en gevolgen van overstromingen in Nederland</Typography>
            <Infographic />
          </StyledSection>
        </div>
      </section>
      <section id="over-lvo" className="brick">
        <section className="section">
          <Box
            className="section-body"
            sx={{
              "&:is(h1, h2, h3, h4, h5, h6, p)": {
                color: theme.palette.lintblauw.main
              }
            }}
          >
            <HKVMarkdown
              key={overLVOMD?.key}
              source={overLVOMD?.source}
              className="rich-text-full"
            />
          </Box>
        </section>
      </section>
      {renderCallToAction()}
      <section id="waarom-lvo" className="brick">
        <section className="section">
          <StyledBox
            className="section-body"
            sx={{
              "&:is(h1, h2, h3, h4, h5, h6, p)": {
                color: theme.palette.lintblauw.main
              }
            }}
          >
            <div>
              <HKVMarkdown
                key={waaromLVOMD?.key}
                source={waaromLVOMD?.source}
                className="rich-text-full"
              />
            </div>
            <div>
              <img
                src="/static/images/overstromingsgevoelig gebied.jpg"
                style={{ width: "100%", objectFit: "contain" }}
              />
            </div>
          </StyledBox>
        </section>
      </section>
      <section id="thema's uitgelicht" className="brick">
        <section className="section section-cards">
          <div className="section-head">
            <Typography variant="h2" color="lintblauw">Thema's uitgelicht</Typography>
          </div>
          {themes?.length > 0 && <div className="section-body">
            <Typography sx={{ marginBottom: theme.spacing(1) }}>Hieronder worden de {themes?.length} thema categorieën binnen het LVO uitgelicht.</Typography>
            {themes.map((th, ix) => (
              <Accordion
                key={ix}
                expanded={expanded === th?.category?.key}
                onChange={(e) => setExpanded(th?.category?.key === expanded ? null : th?.category?.key)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded />}
                  sx={{ minHeight: "fit-content !important" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", padding: theme.spacing(1, 0) }}>
                    <Typography
                      variant="h3"
                      color="hemelblauw"
                    >
                      {th?.category?.name}
                    </Typography>
                    <Typography>{th?.category?.description}</Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails sx={{ margin: theme.spacing(1) }}>
                  <Cards
                    cards={th?.items}
                    columns={3}
                    createLink={createThemeLink}
                    footer={false}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </div>}
        </section>
      </section>
    </div>
  )
}

export default React.memo(Home)